import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TrendingDown, PlayCircle, FileText } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

type Corrida = {
  id: string;
  anio: number;
  mes: number;
  fecha_corte: string;
  total_importe: number;
  total_bienes: number;
  estado: string;
  asiento_id?: string;
};

type Preview = {
  anio: number;
  mes: number;
  fecha_corte: string;
  total_importe: number;
  total_bienes: number;
  por_categoria: { categoria_clave?: string; bienes: number; importe: number }[];
};

export function DepreciacionPage() {
  const qc = useQueryClient();
  const now = new Date();
  const [anio, setAnio] = useState(now.getFullYear());
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [correrSubm, setCorrerSubm] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: corridas, refetch } = useQuery({
    queryKey: ['depre-corridas'],
    queryFn: () => api<Corrida[]>('/depreciacion/corridas'),
  });
  const { data: preview } = useQuery({
    queryKey: ['depre-preview', anio, mes],
    queryFn: () => api<Preview>(`/depreciacion/preview?anio=${anio}&mes=${mes}`),
  });

  async function correr() {
    if (!confirm(`¿Ejecutar corrida de depreciación para ${mes}/${anio}? Esto genera pólizas contables.`)) return;
    setCorrerSubm(true); setErr(null);
    try {
      await api('/depreciacion/correr', { method: 'POST', body: { anio, mes } });
      qc.invalidateQueries({ queryKey: ['depre-corridas'] });
      refetch();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Error');
    } finally {
      setCorrerSubm(false);
    }
  }

  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Recursos Materiales · Bienes</p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Depreciación mensual</h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Método lineal por categoría (guía CONAC). Genera una póliza agrupada por cuenta contable.
        </p>
      </div>

      <Card>
        <CardHeader title="Nueva corrida" subtitle="Selecciona el mes a depreciar; previsualiza antes de ejecutar" />
        <CardBody className="space-y-4">
          <div className="flex items-center gap-3">
            <div>
              <label className="text-[13px] font-medium text-ink">Año</label>
              <input type="number" value={anio} onChange={(e) => setAnio(parseInt(e.target.value) || now.getFullYear())}
                className="num mt-1.5 h-10 w-28 rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle" />
            </div>
            <div>
              <label className="text-[13px] font-medium text-ink">Mes</label>
              <select value={mes} onChange={(e) => setMes(parseInt(e.target.value))}
                className="mt-1.5 h-10 w-36 rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
                {meses.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="ml-auto">
              <Button onClick={correr} loading={correrSubm}>
                <PlayCircle size={14} /> Ejecutar corrida
              </Button>
            </div>
          </div>

          {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}

          {preview && (
            <div className="rounded-lg border border-border bg-canvas p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-mute">Previsualización</p>
                  <p className="num mt-1 text-[22px] font-semibold text-ink">{fmt.money(preview.total_importe)}</p>
                  <p className="text-[12px] text-ink-mute">{preview.total_bienes} bienes a depreciar</p>
                </div>
                <TrendingDown size={30} className="text-amber-500" />
              </div>
              {preview.por_categoria && preview.por_categoria.length > 0 && (
                <table className="mt-3 w-full text-[12px]">
                  <thead className="text-left text-[10px] font-semibold uppercase tracking-wide text-ink-mute">
                    <tr>
                      <th className="pb-2">Categoría</th>
                      <th className="pb-2 text-right">Bienes</th>
                      <th className="pb-2 text-right">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.por_categoria.map((c, i) => (
                      <tr key={i} className="border-t border-border/50">
                        <td className="py-1.5">{c.categoria_clave ?? '—'}</td>
                        <td className="num py-1.5 text-right">{c.bienes}</td>
                        <td className="num py-1.5 text-right font-semibold">{fmt.money(c.importe)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Corridas anteriores" />
        <CardBody className="p-0">
          {!corridas || corridas.length === 0 ? (
            <div className="p-10 text-center text-[13px] text-ink-mute">Aún no se han ejecutado corridas.</div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Periodo</th>
                  <th className="px-5 py-3">Fecha corte</th>
                  <th className="px-5 py-3 text-right">Bienes</th>
                  <th className="px-5 py-3 text-right">Importe</th>
                  <th className="px-5 py-3">Estado</th>
                  <th className="px-5 py-3">Póliza</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {corridas.map((c) => (
                  <tr key={c.id}>
                    <td className="num px-5 py-3 font-semibold">{meses[c.mes-1]} {c.anio}</td>
                    <td className="num px-5 py-3 text-ink-mute">{fmt.date(c.fecha_corte)}</td>
                    <td className="num px-5 py-3 text-right">{c.total_bienes}</td>
                    <td className="num px-5 py-3 text-right font-semibold">{fmt.money(c.total_importe)}</td>
                    <td className="px-5 py-3">
                      <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium',
                        c.estado === 'registrada' ? 'bg-emerald-50 text-emerald-700' :
                        c.estado === 'previsualizada' ? 'bg-amber-50 text-amber-800' :
                        'bg-stone-100 text-ink-mute')}>
                        {c.estado}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {c.asiento_id ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-brand-700">
                          <FileText size={12} /> Vinculada
                        </span>
                      ) : <span className="text-[11px] text-ink-soft">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
